import {
  Component,
  computed,
  input,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ActionBarRight,
  AwActionBarComponent,
  AwBreadCrumbComponent,
  AwDividerComponent,
  AwSpinnerComponent,
  AwToastComponent,
  BreadCrumb,
  SingleSelectOption,
  ToastVariants,
} from '@assetworks-llc/aw-component-lib';
import { AwTextFormComponent } from '../../../../../shared/components/aw-text-form/aw-text-form.component';
import { AwDropdownFormComponent } from '../../../../../shared/components/aw-dropdown-form/aw-dropdown-form.component';
import { FormsModule } from '@angular/forms';

import { EmployeeService } from '../../../../../core/services/employee.service';
import { Employee } from '../../../../../api/models';
import { UpdateEmployee$Params } from '../../../../../api/fn/employee-management/update-employee';
import { catchError, finalize, of } from 'rxjs';

import { UnsavedChangesForm } from '../../../../../shared/unsaved-changes/unsaved-changes-form';
import { UnsavedChangesDialogComponent } from '../../../../../shared/unsaved-changes/unsaved-changes-dialog.component';
@Component({
    selector: 'app-edit-employee-details',
    imports: [
        UnsavedChangesDialogComponent,
    AwDividerComponent,
    AwTextFormComponent,
    AwDropdownFormComponent,
    AwBreadCrumbComponent,
    AwActionBarComponent,
    AwSpinnerComponent,
    AwToastComponent,
    FormsModule
],
    templateUrl: './edit-employee-details.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './edit-employee-details.component.scss',
        '../../../../../../styles/management.scss',
    ]
})
export class EditEmployeeDetailsComponent extends UnsavedChangesForm implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly employeeService = inject(EmployeeService);

  @ViewChild(AwToastComponent) toastComponent!: AwToastComponent;

  isLoading: boolean = false;

  employeeInfo!: Employee | undefined;

  employeeNameInput: string = '';
  employeeIdInput: string = '';
  employeeEmailInput: string = '';
  organizationIdInput: string = '';
  employeeEnabledInput: boolean | null = null;

  statusOptions = signal<SingleSelectOption[]>([
    { label: 'Active', value: true },
    { label: 'Disabled', value: false }
  ]);

  actionsRight = input<ActionBarRight[]>([
    {
      buttonCallback: {
        label: 'Cancel',
        buttonType: 'primary',
        action: () => this.router.navigate([`/employee-management/overview`]),
      },
    },
    {
      buttonCallback: {
        label: 'Save',
        buttonType: 'outlined',
        action: () => {
          this.onEditEmployee();
        },
      },
    },
  ]);

  breadcrumbs = signal<BreadCrumb[]>([
    {
      label: 'Third-Party Employee Management',
      route: '/employee-management/overview',
      newTab: false,
    },
    {
      label: 'Edit Third-Party Employee Details',
      link: `/employee-management/employee-details/edit?employeeName=${this.employeeNameInput}&employeeEmail=${this.employeeEmailInput}&employeeId=${this.employeeIdInput}&organizationId=${this.organizationIdInput}`,
      newTab: false,
    },
  ]);

  ngOnInit(): void {
    this.isLoading = true;

    const queryParams = this.route.snapshot.queryParamMap;

    this.employeeIdInput = queryParams.get('employeeId')!;
    this.employeeNameInput = queryParams.get('employeeName')!;
    this.employeeEmailInput = queryParams.get('employeeEmail')!;
    this.organizationIdInput = queryParams.get('organizationId')!;

    this.employeeService
      .getEmployeesByOrganization({
        organizationId: this.organizationIdInput
      })
      .pipe(
        finalize(() => (this.isLoading = false)),
        catchError(() => {
          this.buildToastMessage('Failed to load third-party employee data', 'error');
          return of([]);
        })
      )
      .subscribe((employees) => {
        const employee = employees?.find(e => e.id === this.employeeIdInput);

        if (employee) {
          this.employeeInfo = employee;
        } else {
          this.buildToastMessage('Third-party employee not found - please select status', 'alert');
          this.employeeInfo = {
            name: this.employeeNameInput,
            id: this.employeeIdInput,
            organizationId: this.organizationIdInput,
            emailAddress: this.employeeEmailInput,
          };
        }
        this.markCleanAfterRender();
      });
  }

  buildToastMessage(message: string, variant: ToastVariants): void {
    this.toastComponent.showToast({
      variant: variant,
      title: message,
      description: '',
      removeIcon: true,
      removeX: false,
      alwaysShow: false,
      toastPositionTopLeft: false,
      multiToasts: false,
    });
  }

  validateEmployeeFields(): boolean {
    return (
      this.employeeEmailInput !== '' &&
      this.employeeEmailInput !== null &&
      this.employeeNameInput !== '' &&
      this.employeeNameInput !== null &&
      this.organizationIdInput !== '' &&
      this.organizationIdInput !== null &&
      this.employeeEnabledInput !== null
    );
  }

  onEditEmployee(): void {
    if (!this.validateEmployeeFields()) {
      this.buildToastMessage('Fill in all required fields including status', 'error');
      return;
    }

    const updateEmployeeParams: UpdateEmployee$Params = {
      organizationId: this.organizationIdInput,
      employeeId: this.employeeIdInput,
      body: {
        emailAddress: this.employeeEmailInput,
        name: this.employeeNameInput,
        enabled: this.employeeEnabledInput ?? true,
      },
    };

    this.isLoading = true;

    this.employeeService
      .updateEmployee(updateEmployeeParams)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((updated) => {
        if (updated) {
          this.buildToastMessage('Successfully updated third-party employee', 'success');
          this.markClean();
        } else {
          this.buildToastMessage('Failed to update third-party employee', 'error');
        }
      });
  }

  onDetailsTextEntrySelected(text: string | null, key: string) {
    if (key === 'employee-name') {
      this.employeeNameInput = text || '';
    } else if (key === 'employee-email') {
      this.employeeEmailInput = text || '';
    } else if (key === 'organization-id') {
      this.organizationIdInput = text || '';
    }
  }

  onEmployeeStatusSelected(option: SingleSelectOption | null) {
    if (option !== null) {
      this.employeeEnabledInput = option.value as boolean;
    }
  }

  employeeTextForms = computed(() => [
    {
      title: 'Name',
      required: true,
      readOnly: false,
      value: this.employeeInfo?.name ?? '',
      textEntryActionHandler: (text: string | null) =>
        this.onDetailsTextEntrySelected(text, 'employee-name'),
    },
    {
      title: 'Email',
      required: true,
      readOnly: false,
      value: this.employeeInfo?.emailAddress ?? '',
      textEntryActionHandler: (text: string | null) =>
        this.onDetailsTextEntrySelected(text, 'employee-email'),
    },
    {
      title: 'Organization ID',
      required: true,
      readOnly: false,
      value: this.employeeInfo?.organizationId ?? '',
      textEntryActionHandler: (text: string | null) =>
        this.onDetailsTextEntrySelected(text, 'organization-id'),
    },
    {
      title: 'ID',
      readOnly: true,
      required: true,
      value: this.employeeInfo?.id ?? '',
      textEntryActionHandler: (text: string | null) => {},
    },
  ]);

  protected formState(): unknown {
    return {
      name: this.employeeNameInput,
      email: this.employeeEmailInput,
      organizationId: this.organizationIdInput,
      enabled: this.employeeEnabledInput,
    };
  }
}
